import { isThisISOWeek, isThisYear, isToday, isYesterday } from 'date-fns';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CgBlock } from 'react-icons/cg';
import { useSelector } from 'react-redux';

import { isNil } from 'lodash-es';
import useTimezone from '../../../hooks/useTimezone';
import useUser from '../../../hooks/useUser';
import {
  ChatMessageType,
  ConversationUserStatusType,
} from '../../../proxies/ChatProxy';
import type { RecentConversationDataType } from '../../../redux/ducks/ChatDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper/EnvironmentHelper';

export type MenuChatListItemProps = {
  data: RecentConversationDataType;
  onChatClick: (id: string) => void;
};

const MAX_NOTIFICATION_AMOUNT = 999999;

const MenuChatListItem: React.FC<MenuChatListItemProps> = ({
  data,
  onChatClick,
}) => {
  const { t, i18n } = useTranslation();
  const { currentTimezone } = useTimezone();
  const { currentUser } = useUser();

  const lastMessageIsMine = useMemo<boolean>(() => {
    if (currentUser && !isNil(data?.last_message)) {
      return data.last_message.user_id === currentUser.id;
    }

    return false;
  }, [currentUser, data]);

  const currentConversation = useSelector(
    (state: RootReducerType) => state.chat.currentConversation,
  );

  const formatLastMessageLabel = useCallback(() => {
    const messageDate = new Date(data?.last_message?.timestamp || 0);

    if (isToday(messageDate)) {
      return Intl.DateTimeFormat(i18n.language, {
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        timeZone: currentTimezone,
      }).format(new Date(messageDate));
    }

    if (isYesterday(messageDate)) {
      return t('static.generic.yesterday');
    }

    if (isThisISOWeek(messageDate)) {
      return t('static.generic.this_week_day', {
        name: Intl.DateTimeFormat(i18n.language, {
          weekday: 'long',
          timeZone: currentTimezone,
        }).format(messageDate),
      });
    }

    if (isThisYear(messageDate)) {
      return Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: currentTimezone,
      }).format(new Date(messageDate));
    }

    return Intl.DateTimeFormat(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      timeZone: currentTimezone,
    }).format(new Date(messageDate));
  }, [data]);

  const formatLastMessageText = useCallback(() => {
    if (isNil(data?.last_message)) {
      return null;
    }

    if (data.last_message.type === ChatMessageType.Text) {
      return data.last_message.text;
    }

    if (data.last_message.type === ChatMessageType.NewAppointment) {
      return lastMessageIsMine
        ? t('static.chat.message_header_appointment_new_mine', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          })
        : t('static.chat.message_header_appointment_new_other', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          });
    }

    if (data.last_message.type === ChatMessageType.AppointmentCanceled) {
      return lastMessageIsMine
        ? t('static.chat.message_header_appointment_canceled_mine', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          })
        : t('static.chat.message_header_appointment_canceled_other', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          });
    }

    if (data.last_message.type === ChatMessageType.AppointmentConfirmed) {
      return lastMessageIsMine
        ? t('static.chat.message_header_appointment_confirmed_mine', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          })
        : t('static.chat.message_header_appointment_confirmed_other', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          });
    }

    if (data.last_message.type === ChatMessageType.AppointmentChangeRequest) {
      return lastMessageIsMine
        ? t('static.chat.message_header_appointment_change_request_mine', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          })
        : t('static.chat.message_header_appointment_change_request_other', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          });
    }

    if (data.last_message.type === ChatMessageType.AppointmentComplete) {
      return lastMessageIsMine
        ? t('static.chat.message_header_appointment_complete_mine', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          })
        : t('static.chat.message_header_appointment_complete_other', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          });
    }

    if (data.last_message.type === ChatMessageType.AppointmentCancelRequest) {
      return lastMessageIsMine
        ? t('static.chat.message_header_appointment_cancel_request_mine', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          })
        : t('static.chat.message_header_appointment_cancel_request_other', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          });
    }

    if (data.last_message.type === ChatMessageType.AppointmentChangeDenied) {
      return lastMessageIsMine
        ? t('static.chat.message_header_appointment_change_denied_mine', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          })
        : t('static.chat.message_header_appointment_change_denied_other', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          });
    }

    if (data.last_message.type === ChatMessageType.AppointmentDenied) {
      return lastMessageIsMine
        ? t('static.chat.message_header_appointment_new_denied_mine', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          })
        : t('static.chat.message_header_appointment_new_denied_other', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          });
    }

    if (data.last_message.type === ChatMessageType.AppointmentCancelDenied) {
      return lastMessageIsMine
        ? t('static.chat.message_header_appointment_cancel_denied_mine', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          })
        : t('static.chat.message_header_appointment_cancel_denied_other', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          });
    }

    if (data.last_message.type === ChatMessageType.AppointmentChanged) {
      return lastMessageIsMine
        ? t('static.chat.message_header_appointment_changed_mine', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          })
        : t('static.chat.message_header_appointment_changed_other', {
            name: `${data.user?.first_name} ${data.user?.last_name}`,
          });
    }

    if (data.last_message.type === ChatMessageType.PushEnableTip) {
      return 'System';
    }

    return null;
  }, [data, lastMessageIsMine, t]);

  const generateInnerContent = useCallback(
    () => (
      <div
        className={`flex min-w-full flex-row items-start justify-between gap-1 ${
          data.blocked && 'text-slate-500'
        }`}
      >
        <span className="flex min-h-[48px] flex-col items-start justify-start">
          <span className="relative flex min-h-[48px] min-w-[48px] flex-col items-center justify-center rounded-full bg-slate-300 text-center shadow-sm">
            <span
              className={`${
                data.user?.media?.profile_picture ? 'hidden' : 'flex'
              } size-full min-h-full flex-col items-center justify-center text-center text-xs font-bold capitalize`}
            >
              {`${data.user.first_name.substring(
                0,
                1,
              )}${data.user.last_name.substring(0, 1)}`}
            </span>
            {data.user?.media?.profile_picture && (
              <img
                alt={`${data.user.first_name
                  .substring(0, 1)
                  .toUpperCase()}${data.user.last_name
                  .substring(0, 1)
                  .toUpperCase()}`}
                style={{
                  objectFit: 'cover',
                  minHeight: '100%',
                  minWidth: '100%',
                }}
                src={`//${getEnvironmentVariable('REACT_APP_CDN_API')}${
                  data.user?.media?.profile_picture
                }`}
                className="absolute left-0 top-0 size-[32px] rounded-full"
              />
            )}
            {data.blocked ? (
              <span className="absolute left-0 top-0 flex size-[48px] flex-col items-center justify-center rounded-full bg-white/75 text-slate-400">
                <CgBlock size={48} />
              </span>
            ) : (
              data.user.status === ConversationUserStatusType.Online && (
                <span className="absolute bottom-0 right-0 m-0 size-[6px] rounded-full bg-green-500 p-px">
                  &nbsp;
                </span>
              )
            )}
          </span>
        </span>
        <span className="flex min-h-[48px] grow flex-col items-start justify-center pl-1 text-sm">
          <span className="flex min-w-full flex-row items-start justify-between">
            <span className="line-clamp-1 grow overflow-hidden text-ellipsis break-all font-bold text-left">
              {`${data.user.first_name} ${data.user.last_name}`}
            </span>

            <span
              className={`text-xs capitalize text-right ${
                data.new_messages > 0 && 'font-bold'
              }`}
            >
              {formatLastMessageLabel()}
            </span>
          </span>
          <span className="flex min-h-[18px] grow min-w-full flex-row items-start justify-between gap-1">
            <span
              className={`line-clamp-1 grow overflow-hidden text-ellipsis break-all py-[2px] text-xs text-left ${
                data.new_messages > 0 && 'grow font-bold'
              }`}
            >
              {formatLastMessageText()}
            </span>
            {data.new_messages > 0 && (
              <span className="flex w-[64px] flex-row items-end justify-end">
                <span className="min-w-[20px] rounded-full bg-sky-600 p-[2px] px-[4px] text-center text-xs font-extrabold text-white">
                  {data.new_messages > MAX_NOTIFICATION_AMOUNT
                    ? `${MAX_NOTIFICATION_AMOUNT}+`
                    : data.new_messages}
                </span>
              </span>
            )}
          </span>
        </span>
      </div>
    ),
    [data, formatLastMessageText, formatLastMessageLabel],
  );

  return currentConversation && data.id === currentConversation ? (
    <button
      id={`menu-chat-conversation-${data.id}`}
      data-test-id={`menu-chat-conversation-${data.id}`}
      key={`menu-chat-conversation-${data.id}`}
      aria-label={`menu-chat-conversation-${data.id}`}
      data-umami-event="menu-chat-conversation-selected"
      data-umami-event-conversation={`conversation-${data.id}`}
      className="flex size-full min-w-full flex-col items-center justify-center"
      type="button"
      onClick={() => {
        onChatClick(data.id);
      }}
    >
      <div className="size-full p-2 hover:bg-slate-100 lg:bg-slate-100">
        {generateInnerContent()}
      </div>
    </button>
  ) : (
    <div className="flex size-full min-w-full flex-col items-center justify-center">
      <button
        id={`menu-chat-conversation-${data.id}`}
        data-test-id={`menu-chat-conversation-${data.id}`}
        key={`menu-chat-conversation-${data.id}`}
        aria-label={`menu-chat-conversation-${data.id}`}
        data-umami-event="menu-chat-conversation-selected"
        data-umami-event-conversation={`conversation-${data.id}`}
        className="size-full cursor-pointer p-2 hover:bg-slate-100"
        type="button"
        onClick={() => {
          onChatClick(data.id);
        }}
      >
        {generateInnerContent()}
      </button>
    </div>
  );
};

export default MenuChatListItem;
